<template>
  <van-popup
    :show="modelValue"
    position="bottom"
    round
    safe-area-inset-bottom
    @update:show="emit('update:modelValue', $event)"
  >
    <section class="area-selector">
      <header class="area-selector__header">
        <span class="area-selector__header-spacer" />
        <div data-strong>{{ title }}</div>
        <button type="button" @click="emit('update:modelValue', false)">
          <app-image class="app-cancel-icon" :src="cancelIcon" alt="" />
        </button>
      </header>

      <div v-if="mode === 'country'" class="area-selector__country-list">
        <button
          v-for="(item, index) in items"
          :key="item.countryId || item.iso_code_2 || index"
          type="button"
          class="area-selector__country-item"
          @click="selectItem(item, index)"
        >
          <span>{{ displayCountryName(item) }}</span>
          <em>{{ item?.iso_code_2 || '' }}</em>
        </button>
      </div>

      <div v-else class="area-selector__body">
        <div ref="scrollRef" class="area-selector__list">
          <template v-for="group in groupedItems" :key="group.letter">
            <div :id="`letter-${group.letter}`" class="area-selector__letter">
              {{ group.letter }}
            </div>
            <button
              v-for="entry in group.items"
              :key="entry.key"
              type="button"
              class="area-selector__area-item"
              @click="selectItem(entry.item, entry.originIndex)"
            >
              {{ displayAreaName(entry.item) }}
            </button>
          </template>
        </div>

        <aside v-if="letters.length" class="area-selector__index">
          <button
            v-for="letter in letters"
            :key="letter"
            type="button"
            :class="{ active: activeLetter === letter }"
            @click="scrollToLetter(letter)"
          >
            {{ letter }}
          </button>
        </aside>
      </div>
    </section>
  </van-popup>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getLocalizedAreaName } from '@/utils/display-name';
import cancelIcon from '@/assets/images/cancel_gray.png';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: ''
  },
  items: {
    type: Array,
    default: () => []
  },
  mode: {
    type: String,
    default: 'country'
  }
});

const emit = defineEmits(['update:modelValue', 'select']);
const { locale } = useI18n();

const scrollRef = ref(null);
const activeLetter = ref('');
let isScrollingByClick = false;

function areaGroupLetter(item) {
  const nameEn = String(item?.nameEn || '').trim().toUpperCase();
  const letter = nameEn.slice(0, 1);
  return /^[A-Z]$/.test(letter) ? letter : '#';
}

function areaSortName(item) {
  return String(item?.nameEn || '').trim().toUpperCase();
}

const sortedAreaItems = computed(() => {
  return props.items
    .map((item, originIndex) => ({
      item,
      originIndex,
      key: item?.cityId || item?.provinceId || originIndex
    }))
    .sort((a, b) => {
      const aLetter = areaGroupLetter(a.item);
      const bLetter = areaGroupLetter(b.item);
      if (aLetter !== bLetter) {
        if (aLetter === '#') return 1;
        if (bLetter === '#') return -1;
        return aLetter.localeCompare(bLetter);
      }

      const nameCompare = areaSortName(a.item).localeCompare(areaSortName(b.item));
      if (nameCompare !== 0) {
        return nameCompare;
      }

      return String(a.item?.nameCn || '').localeCompare(String(b.item?.nameCn || ''));
    });
});

const groupedItems = computed(() => {
  const groups = [];
  for (const entry of sortedAreaItems.value) {
    const letter = areaGroupLetter(entry.item);
    const current = groups[groups.length - 1];
    if (!current || current.letter !== letter) {
      groups.push({ letter, items: [entry] });
    } else {
      current.items.push(entry);
    }
  }
  return groups;
});

const letters = computed(() => groupedItems.value.map((group) => group.letter));

watch(() => props.modelValue, async (visible) => {
  if (!visible || props.mode === 'country') {
    return;
  }

  await nextTick();
  activeLetter.value = letters.value[0] || '';
  bindScrollListener();
});

watch(() => props.items, async () => {
  if (!props.modelValue || props.mode === 'country') {
    return;
  }

  await nextTick();
  activeLetter.value = letters.value[0] || '';
});

function bindScrollListener() {
  const el = scrollRef.value;
  if (!el || el.dataset.bound === '1') {
    return;
  }

  el.dataset.bound = '1';
  el.addEventListener('scroll', handleScroll, { passive: true });
}

function handleScroll() {
  if (isScrollingByClick) {
    return;
  }

  const container = scrollRef.value;
  if (!container) {
    return;
  }

  let current = letters.value[0] || '';
  for (const letter of letters.value) {
    const section = container.querySelector(`#letter-${letter}`);
    if (!section) {
      continue;
    }

    if (container.scrollTop >= section.offsetTop - 8) {
      current = letter;
    } else {
      break;
    }
  }

  activeLetter.value = current;
}

function scrollToLetter(letter) {
  const container = scrollRef.value;
  const section = container?.querySelector(`#letter-${letter}`);
  if (!container || !section) {
    return;
  }

  isScrollingByClick = true;
  activeLetter.value = letter;
  container.scrollTo({
    top: section.offsetTop,
    behavior: 'smooth'
  });
  setTimeout(() => {
    isScrollingByClick = false;
  }, 300);
}

function selectItem(item, index) {
  emit('select', { item, index });
  emit('update:modelValue', false);
}

function displayCountryName(item) {
  return getLocalizedAreaName(item, locale.value) || '--';
}

function displayAreaName(item) {
  return getLocalizedAreaName(item, locale.value) || '--';
}

onBeforeUnmount(() => {
  const el = scrollRef.value;
  if (el) {
    el.removeEventListener('scroll', handleScroll);
  }
});
</script>

<style scoped lang="scss">
.area-selector {
  display: flex;
  height: 60vh;
  flex-direction: column;
  background: #ffffff;
}

.area-selector__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 16px;
  border-bottom: 1px solid #f5f5f5;

  [data-strong] {
    color: #1f2430;
    font-size: 18px;
    font-weight: 700;
  }

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    padding: 0;
    border: 0;
    background: transparent;

    .app-image {
      display: block;
      width: 18px;
      height: 18px;
    }
  }
}

.area-selector__header-spacer {
  width: 18px;
  height: 18px;
}

.area-selector__country-list {
  flex: 1;
  overflow-y: auto;
  border-top: 1px solid #f5f5f5;
}

.area-selector__country-item,
.area-selector__area-item {
  display: flex;
  width: 100%;
  min-height: 56px;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border: 0;
  border-bottom: 1px solid #f5f5f5;
  background: #ffffff;
  color: #333333;
  text-align: left;
  font-size: 16px;
}

.area-selector__country-item em {
  color: #999999;
  font-style: normal;
}

.area-selector__body {
  display: flex;
  min-height: 0;
  flex: 1;
}

.area-selector__list {
  flex: 1;
  overflow-y: auto;
}

.area-selector__letter {
  display: flex;
  height: 32px;
  align-items: center;
  padding: 0 16px;
  border-top: 1px solid #f5f5f5;
  color: #666666;
  font-size: 14px;
}

.area-selector__index {
  display: flex;
  width: 32px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  background: #ffffff;

  button {
    display: flex;
    width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
    margin: 1px 0;
    padding: 0;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: #666666;
    font-size: 12px;

    &.active {
      background: #2464F8;
      color: #ffffff;
    }
  }
}
</style>
